import React, { useState, useEffect } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import { vehiclesAPI } from '../services/api';
import VehicleLiabilities from '../components/VehicleLiabilities';

const Vehicles = ({ selectedBusiness }) => {
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [showLiabilities, setShowLiabilities] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [filters, setFilters] = useState({
    vehicle_status: '',
    vehicle_type: ''
  });
  const [formData, setFormData] = useState({
    vehicle_name: '',
    make: '',
    model: '',
    year: '',
    vin: '',
    license_plate: '',
    vehicle_type: 'sedan',
    vehicle_status: 'active',
    color: '',
    mileage: '',
    purchase_date: '',
    purchase_price: '',
    current_value: '',
    insurance_provider: '',
    insurance_policy_number: '',
    registration_expires: '',
    photo_url: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedBusiness, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        ...(selectedBusiness && { business_id: selectedBusiness }),
        ...filters
      };
      
      const [vehiclesRes, statsRes] = await Promise.all([
        vehiclesAPI.getAll(params),
        vehiclesAPI.getSummary(params)
      ]);
      
      setVehicles(vehiclesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load vehicles:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fmtCurrency = (val) => {
    try {
      const n = parseFloat(val);
      if (Number.isNaN(n)) return 'N/A';
      return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch {
      return 'N/A';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        business_id: selectedBusiness || null
      };

      if (editingVehicle) {
        await vehiclesAPI.update(editingVehicle.id, dataToSubmit);
      } else {
        await vehiclesAPI.create(dataToSubmit);
      }
      
      setShowModal(false);
      setEditingVehicle(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to save vehicle:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to save vehicle');
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_name: '',
      make: '',
      model: '',
      year: '',
      vin: '',
      license_plate: '',
      color: '',
      vehicle_type: 'sedan',
      ownership_type: 'owned',
      purchase_date: '',
      purchase_price: '',
      current_value: '',
      sale_date: '',
      sale_price: '',
      initial_odometer: '',
      current_odometer: '',
      odometer_unit: 'miles',
      registration_number: '',
      registration_state: '',
      registration_expiration: '',
      title_number: '',
      title_state: '',
      insurance_company: '',
      insurance_policy_number: '',
      insurance_expiration: '',
      insurance_premium: '',
      insurance_frequency: 'monthly',
      fuel_type: 'gasoline',
      tank_capacity: '',
      mpg_city: '',
      mpg_highway: '',
      mpg_average: '',
      monthly_payment: '',
      remaining_payments: '',
      payoff_amount: '',
      last_service_date: '',
      last_service_odometer: '',
      next_service_date: '',
      next_service_odometer: '',
      is_active: true,
      garage_location: '',
      photo_url: '',
      notes: '',
      custom_fields: null
    });
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      vehicle_name: vehicle.vehicle_name || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      vin: vehicle.vin || '',
      license_plate: vehicle.license_plate || '',
      color: vehicle.color || '',
      vehicle_type: vehicle.vehicle_type || 'sedan',
      ownership_type: vehicle.ownership_type || 'owned',
      purchase_date: vehicle.purchase_date?.split('T')[0] || '',
      purchase_price: vehicle.purchase_price || '',
      current_value: vehicle.current_value || '',
      sale_date: vehicle.sale_date?.split('T')[0] || '',
      sale_price: vehicle.sale_price || '',
      initial_odometer: vehicle.initial_odometer || '',
      current_odometer: vehicle.current_odometer || '',
      odometer_unit: vehicle.odometer_unit || 'miles',
      registration_number: vehicle.registration_number || '',
      registration_state: vehicle.registration_state || '',
      registration_expiration: vehicle.registration_expiration?.split('T')[0] || '',
      title_number: vehicle.title_number || '',
      title_state: vehicle.title_state || '',
      insurance_company: vehicle.insurance_company || '',
      insurance_policy_number: vehicle.insurance_policy_number || '',
      insurance_expiration: vehicle.insurance_expiration?.split('T')[0] || '',
      insurance_premium: vehicle.insurance_premium || '',
      insurance_frequency: vehicle.insurance_frequency || 'monthly',
      fuel_type: vehicle.fuel_type || 'gasoline',
      tank_capacity: vehicle.tank_capacity || '',
      mpg_city: vehicle.mpg_city || '',
      mpg_highway: vehicle.mpg_highway || '',
      mpg_average: vehicle.mpg_average || '',
      monthly_payment: vehicle.monthly_payment || '',
      remaining_payments: vehicle.remaining_payments || '',
      payoff_amount: vehicle.payoff_amount || '',
      last_service_date: vehicle.last_service_date?.split('T')[0] || '',
      last_service_odometer: vehicle.last_service_odometer || '',
      next_service_date: vehicle.next_service_date?.split('T')[0] || '',
      next_service_odometer: vehicle.next_service_odometer || '',
      is_active: vehicle.is_active !== undefined ? vehicle.is_active : true,
      garage_location: vehicle.garage_location || '',
      photo_url: vehicle.photo_url || '',
      notes: vehicle.notes || '',
      custom_fields: vehicle.custom_fields || null
    });
    setShowModal(true);
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    
    try {
      await vehiclesAPI.delete(vehicleId);
      loadData();
    } catch (error) {
      console.error('Failed to delete vehicle:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert('Failed to delete vehicle');
    }
  };

  const handleViewProfile = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowProfileCard(true);
  };

  const handleViewLiabilities = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowLiabilities(true);
  };

  if (loading) {
    return <div className="p-6">Loading vehicles...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Vehicles</h1>
            <p className="text-gray-600 mt-1">Manage your vehicle fleet and track expenses</p>
          </div>
          <button
            onClick={() => {
              setEditingVehicle(null);
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            + Add Vehicle
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Vehicles</div>
              <div className="text-2xl font-bold text-gray-800">{stats.total_vehicles || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Active Vehicles</div>
              <div className="text-2xl font-bold text-green-600">{stats.active_vehicles || 0}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Total Value</div>
              <div className="text-2xl font-bold text-blue-600">
                ${parseFloat(stats.total_value || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-600">Avg Mileage</div>
              <div className="text-2xl font-bold text-purple-600">
                {parseFloat(stats.avg_mileage || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              value={filters.vehicle_status}
              onChange={(e) => setFilters({ ...filters, vehicle_status: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
              <option value="sold">Sold</option>
            </select>

            <select
              value={filters.vehicle_type}
              onChange={(e) => setFilters({ ...filters, vehicle_type: e.target.value })}
              className="border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Types</option>
              <option value="sedan">Sedan</option>
              <option value="suv">SUV</option>
              <option value="truck">Truck</option>
              <option value="van">Van</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">License</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Mileage</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {vehicles.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                  No vehicles found. Click "Add Vehicle" to create one.
                </td>
              </tr>
            ) : (
              vehicles.map((vehicle) => (
                <tr key={vehicle.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <ErrorBoundary fallback={<div className="text-red-600 text-xs">Error</div>}>
                      <div className="font-medium text-gray-900">
                        {(vehicle.year || '') + ' ' + (vehicle.make || '') + ' ' + (vehicle.model || '')}
                      </div>
                      <div className="text-sm text-gray-500">{vehicle.vehicle_name || 'No name'}</div>
                    </ErrorBoundary>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 capitalize">
                    <ErrorBoundary fallback={<span className="text-red-600 text-xs">Error</span>}>
                      {vehicle.vehicle_type || '—'}
                    </ErrorBoundary>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <ErrorBoundary fallback={<span className="text-red-600 text-xs">Error</span>}>
                      {vehicle.license_plate || 'N/A'}
                    </ErrorBoundary>
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">
                    <ErrorBoundary fallback={<span className="text-red-600 text-xs">—</span>}>
                      {vehicle.current_odometer ? parseFloat(vehicle.current_odometer).toLocaleString() : 'N/A'}
                    </ErrorBoundary>
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900">
                    <ErrorBoundary fallback={<span className="text-red-600 text-xs">—</span>}>
                      {fmtCurrency(vehicle.current_value)}
                    </ErrorBoundary>
                  </td>
                  <td className="px-6 py-4">
                    <ErrorBoundary fallback={<span className="text-red-600 text-xs">—</span>}>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        vehicle.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {vehicle.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </ErrorBoundary>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <ErrorBoundary fallback={<span className="text-red-600 text-xs">Actions error</span>}>
                      <button
                        onClick={() => handleDelete(vehicle.id)}
                        className="text-red-600 hover:text-red-800 mr-5"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => handleViewLiabilities(vehicle)}
                        className="text-purple-600 hover:text-purple-800 mr-3 font-medium"
                        title="Manage Vehicle Liabilities"
                      >
                        💰 Liabilities
                      </button>
                      <button
                        onClick={() => handleEdit(vehicle)}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleViewProfile(vehicle)}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded font-medium transition-colors shadow-sm"
                        title="View Vehicle Profile"
                      >
                        🚗 Profile
                      </button>
                    </ErrorBoundary>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Name</label>
                  <input
                    type="text"
                    value={formData.vehicle_name}
                    onChange={(e) => setFormData({ ...formData, vehicle_name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="My Car"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Make *</label>
                  <input
                    type="text"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                    placeholder="Toyota"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                    placeholder="Camry"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                    min="1900"
                    max="2030"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VIN</label>
                  <input
                    type="text"
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    maxLength="17"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License Plate</label>
                  <input
                    type="text"
                    value={formData.license_plate}
                    onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={formData.vehicle_type}
                    onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="truck">Truck</option>
                    <option value="van">Van</option>
                    <option value="motorcycle">Motorcycle</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ownership Type</label>
                  <select
                    value={formData.ownership_type}
                    onChange={(e) => setFormData({ ...formData, ownership_type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="owned">Owned</option>
                    <option value="leased">Leased</option>
                    <option value="financed">Financed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Odometer</label>
                  <input
                    type="number"
                    value={formData.current_odometer}
                    onChange={(e) => setFormData({ ...formData, current_odometer: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    min="0"
                    placeholder="Current mileage"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price</label>
                  <input
                    type="number"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Value</label>
                  <input
                    type="number"
                    value={formData.current_value}
                    onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Company</label>
                  <input
                    type="text"
                    value={formData.insurance_company}
                    onChange={(e) => setFormData({ ...formData, insurance_company: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
                  <input
                    type="text"
                    value={formData.insurance_policy_number}
                    onChange={(e) => setFormData({ ...formData, insurance_policy_number: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Expiration</label>
                  <input
                    type="date"
                    value={formData.registration_expiration}
                    onChange={(e) => setFormData({ ...formData, registration_expiration: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Active Status</label>
                  <select
                    value={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Photo URL (Web Image Link)
                    <span className="ml-2 text-xs text-blue-600 font-normal">(Shows in profile card)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.photo_url || ''}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="https://photos.example.com/image.webp or .jpg or .png"
                    autoComplete="off"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Web image link (supports .jpg, .png, .webp, etc.)
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingVehicle(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingVehicle ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vehicle Profile Card Modal - Government ID Style */}
      {showProfileCard && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="relative bg-gradient-to-br from-blue-50 to-gray-50 rounded-xl shadow-2xl max-w-4xl w-full border-4 border-blue-900">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowProfileCard(false);
                setSelectedVehicle(null);
              }}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* ID Card Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-4 rounded-t-xl border-b-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-yellow-500 rounded-full p-2">
                    <svg className="w-8 h-8 text-blue-900" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider">Vehicle Profile</h3>
                    <p className="text-xs text-blue-200">Vehicle Details Card</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-200">ID No.</p>
                  <p className="text-lg font-bold tracking-wider">#{selectedVehicle.id}</p>
                </div>
              </div>
            </div>

            {/* Main ID Card Body */}
            <div className="p-8">
              <div className="grid grid-cols-3 gap-6">
                {/* Left Column - Photo */}
                <div className="col-span-1">
                  <div className="bg-white rounded-lg border-4 border-gray-300 overflow-hidden shadow-lg">
                    {selectedVehicle.photo_url ? (
                      <img 
                        src={selectedVehicle.photo_url} 
                        alt={selectedVehicle.vehicle_name}
                        className="w-full h-64 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={selectedVehicle.photo_url ? "hidden" : "flex w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 items-center justify-center"}>
                      <div className="text-center text-gray-500">
                        <svg className="w-20 h-20 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                        </svg>
                        <p className="text-xs font-semibold uppercase">No Photo</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick Stats Below Photo */}
                  <div className="mt-4 bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                    <div className="text-center">
                      <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Mileage</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {selectedVehicle.current_odometer ? parseInt(selectedVehicle.current_odometer).toLocaleString() : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-600 uppercase">{selectedVehicle.odometer_unit || 'miles'}</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Vehicle Details */}
                <div className="col-span-2 space-y-4">
                  {/* Vehicle Name */}
                  <div className="bg-white rounded-lg border-2 border-blue-900 p-4 shadow-md">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Vehicle Name</p>
                    <h2 className="text-2xl font-bold text-blue-900 uppercase tracking-wide">{selectedVehicle.vehicle_name}</h2>
                  </div>

                  {/* Make/Model/Year */}
                  <div className="bg-white rounded-lg border-2 border-gray-300 p-4 shadow">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-2">Vehicle Information</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
                    </p>
                  </div>

                  {/* Key Details Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">VIN</p>
                      <p className="text-sm font-bold text-gray-900">{selectedVehicle.vin || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">License Plate</p>
                      <p className="text-sm font-bold text-gray-900">{selectedVehicle.license_plate || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Type</p>
                      <p className="text-sm font-bold text-gray-900 capitalize">{selectedVehicle.vehicle_type || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Color</p>
                      <p className="text-sm font-bold text-gray-900 capitalize">{selectedVehicle.color || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Ownership</p>
                      <p className="text-sm font-bold text-gray-900 capitalize">{selectedVehicle.ownership_type || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg border-2 border-gray-300 p-3 shadow">
                      <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Status</p>
                      <p className="text-sm font-bold text-gray-900 uppercase">
                        {selectedVehicle.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </p>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-600 p-4 shadow-md">
                    <p className="text-xs text-gray-600 uppercase tracking-wider font-semibold mb-3">Financial Information</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Purchase Price</p>
                        <p className="text-base font-bold text-gray-900">
                          {selectedVehicle.purchase_price ? `$${parseFloat(selectedVehicle.purchase_price).toLocaleString()}` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 uppercase tracking-wider">Current Value</p>
                        <p className="text-base font-bold text-green-700">
                          {selectedVehicle.current_value ? `$${parseFloat(selectedVehicle.current_value).toLocaleString()}` : 'N/A'}
                        </p>
                      </div>
                      {selectedVehicle.monthly_payment && (
                        <>
                          <div>
                            <p className="text-xs text-gray-600 uppercase tracking-wider">Monthly Payment</p>
                            <p className="text-base font-bold text-red-700">
                              ${parseFloat(selectedVehicle.monthly_payment).toLocaleString()}
                            </p>
                          </div>
                          {selectedVehicle.payoff_amount && (
                            <div>
                              <p className="text-xs text-gray-600 uppercase tracking-wider">Payoff Amount</p>
                              <p className="text-base font-bold text-gray-900">
                                ${parseFloat(selectedVehicle.payoff_amount).toLocaleString()}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Insurance Info */}
                  {selectedVehicle.insurance_company && (
                    <div className="bg-yellow-50 rounded-lg border-2 border-yellow-400 p-4 shadow">
                      <p className="text-xs text-gray-600 uppercase tracking-wider font-semibold mb-2">Insurance</p>
                      <p className="text-sm font-semibold text-gray-900">{selectedVehicle.insurance_company}</p>
                      {selectedVehicle.insurance_policy_number && (
                        <p className="text-xs text-gray-600 mt-1">Policy: {selectedVehicle.insurance_policy_number}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Notes Section (if exists) */}
              {selectedVehicle.notes && (
                <div className="mt-6 bg-yellow-50 rounded-lg border-2 border-yellow-400 p-4 shadow">
                  <p className="text-xs text-gray-600 uppercase tracking-wider font-semibold mb-2">Additional Notes</p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{selectedVehicle.notes}</p>
                </div>
              )}
            </div>

            {/* ID Card Footer */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-8 py-3 rounded-b-xl border-t-2 border-yellow-500">
              {/* Blank footer bar */}
            </div>
          </div>
        </div>
      )}

      {/* Liabilities Modal */}
      {showLiabilities && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Vehicle Liabilities</h2>
              <button
                onClick={() => {
                  setShowLiabilities(false);
                  setSelectedVehicle(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <VehicleLiabilities
              vehicleId={selectedVehicle.id}
              vehicleName={selectedVehicle.vehicle_name || `${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}`}
              selectedBusiness={selectedBusiness}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
